import { Helmet } from 'react-helmet-async';

// Single source of truth for per-page SEO. Every route renders one <Seo/>.
// The static index.html deliberately carries NO description / canonical / og /
// twitter tags — those are owned here so there's exactly one of each after
// render (avoids duplicate tags once Helmet reconciles the head).

const SITE_URL = 'https://localareacloud.com';
const SITE_NAME = 'Local Area Cloud';
const DEFAULT_IMAGE = `${SITE_URL}/og-image.png`;

type SeoProps = {
  title: string;
  description: string;
  /** Path portion of the canonical URL, e.g. '/privacy'. Defaults to '/'. */
  path?: string;
  image?: string;
  /** Keep this page out of the index (app / auth screens). */
  noindex?: boolean;
  /** og:type — 'website' for pages, 'article' for posts. */
  type?: string;
  /** JSON-LD structured data (object or array of objects). */
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
};

export function Seo({
  title,
  description,
  path = '/',
  image = DEFAULT_IMAGE,
  noindex = false,
  type = 'website',
  jsonLd,
}: SeoProps) {
  const canonical = path === '/' ? SITE_URL : `${SITE_URL}${path}`;
  const blocks = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];

  return (
    <Helmet prioritizeSeoTags>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />
      <meta
        name="robots"
        content={noindex ? 'noindex,nofollow' : 'index,follow'}
      />

      {/* Open Graph */}
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={image} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />

      {blocks.map((block, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(block)}
        </script>
      ))}
    </Helmet>
  );
}
